import { z } from "zod";
import i18n from "../config/i18n";
import { VALIDATION } from "../config/constants";

const t = (key: string) => i18n.t(key);

// Reuse existing regex patterns from registerSchema
const PHONE_REGEX = /^\+?[0-9\-\s()]{7,20}$/;

// Base schema for common user fields
const getBaseUserSchema = () =>
  z.object({
    firstName: z
      .string()
      .min(1, t("validation.firstNameRequired"))
      .min(2, t("validation.firstNameMin"))
      .max(50, t("validation.firstNameMax")),
    lastName: z
      .string()
      .min(1, t("validation.lastNameRequired"))
      .min(2, t("validation.lastNameMin"))
      .max(50, t("validation.lastNameMax")),
    phoneNumber: z
      .string()
      .optional()
      .refine(
        (val) => !val || PHONE_REGEX.test(val),
        t("validation.phoneInvalid"),
      ),
    country: z.string().optional(),
    city: z.string().optional(),
    isActive: z.boolean().default(true),
    isEmailVerified: z.boolean().default(false),
    roleIds: z.array(z.string()).default([]),
  });

// Schema for creating new users
export const getCreateUserSchema = () =>
  getBaseUserSchema()
    .extend({
      email: z
        .string()
        .min(1, t("validation.emailRequired"))
        .email(t("validation.emailInvalid")),
      authType: z.enum(["manual", "google", "admin"]).default("manual"),
      password: z.string().optional(),
    })
    .refine(
      (data) => {
        // If authType is manual, password is required
        if (data.authType === "manual") {
          return data.password && data.password.length >= VALIDATION.PASSWORD_MIN_LENGTH;
        }
        return true;
      },
      {
        message: t("validation.passwordLocalRequired"),
        path: ["password"],
      },
    )
    .refine((data) => !data.password || data.password.length >= VALIDATION.PASSWORD_MIN_LENGTH, {
      message: t("validation.passwordMin"),
      path: ["password"],
    });

// Schema for updating existing users (email is now editable)
export const getUpdateUserSchema = () =>
  getBaseUserSchema().extend({
    email: z
      .string()
      .min(1, t("validation.emailRequired"))
      .email(t("validation.emailInvalid")),
    authType: z.enum(["manual", "google", "admin"]).optional(),
  });

// Dropshipping Order Item Validation Schemas
export const getUpdateOrderItemSchema = () =>
  z.object({
    orderItemId: z.string().min(1, t("validation.orderItemIdRequired")),
    retailer: z
      .string()
      .max(50, t("validation.retailerMax"))
      .optional(),
    itemStatusId: z
      .number()
      .positive(t("validation.statusIdPositive"))
      .optional(),
    notes: z.string().max(500, t("validation.notesMax")).optional(),
    trackingNumber: z
      .string()
      .max(100, t("validation.trackingNumberMax"))
      .optional(),
    estimatedDelivery: z.string().datetime(t("validation.invalidDateFormat")).optional(),
    orderedAt: z.string().datetime(t("validation.invalidDateFormat")).optional(),
    shippedAt: z.string().datetime(t("validation.invalidDateFormat")).optional(),
  });

export const getMarkAsShippedSchema = () =>
  z.object({
    trackingNumber: z
      .string()
      .min(1, t("validation.trackingNumberRequired"))
      .max(100, t("validation.trackingNumberMax")),
    notes: z.string().max(500, t("validation.notesMax")).optional(),
  });

export const getUpdateItemStatusSchema = () =>
  z.object({
    orderItemId: z.string().min(1, t("validation.orderItemIdRequired")),
    newStatusId: z.number().positive(t("validation.statusPositive")),
    notes: z.string().max(500, t("validation.notesMax")).optional(),
  });

/** @deprecated Use getCreateUserSchema() for i18n support */
export const createUserSchema = getCreateUserSchema();
/** @deprecated Use getUpdateUserSchema() for i18n support */
export const updateUserSchema = getUpdateUserSchema();
/** @deprecated Use getUpdateOrderItemSchema() for i18n support */
export const updateOrderItemSchema = getUpdateOrderItemSchema();
/** @deprecated Use getMarkAsShippedSchema() for i18n support */
export const markAsShippedSchema = getMarkAsShippedSchema();
/** @deprecated Use getUpdateItemStatusSchema() for i18n support */
export const updateItemStatusSchema = getUpdateItemStatusSchema();

// Type exports
export type CreateUserFormData = z.infer<ReturnType<typeof getCreateUserSchema>>;
export type UpdateUserFormData = z.infer<ReturnType<typeof getUpdateUserSchema>>;

// Validation helper functions
export const validateCreateUser = (data: unknown) => {
  return getCreateUserSchema().safeParse(data);
};

export const validateUpdateUser = (data: unknown) => {
  return getUpdateUserSchema().safeParse(data);
};

// Helper to get field-specific errors
export const getFieldErrors = (
  result: z.SafeParseReturnType<unknown, unknown>,
) => {
  if (!result.success) {
    const errors: Record<string, string> = {};
    result.error.errors.forEach((err) => {
      if (err.path.length > 0) {
        const path = err.path[0] as string;
        errors[path] = err.message;
      }
    });
    return errors;
  }
  return {};
};
